import { AlertCircle, CheckCircle2, ClipboardCheck, Send } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  useGetCheckinConfigQuery,
  useSubmitFridayCheckinMutation,
} from '@/services/api/fridayCheckinService';
import { selectUser } from '@/state/selectors/root-selectors';

const LOCALSTORAGE_KEY = 'checkinDismissedDate';

function getCurrentEasternTime(): Date {
  return new Date(
    new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }),
  );
}

function getEasternDateKey(): string {
  const et = getCurrentEasternTime();
  const year = et.getFullYear();
  const month = String(et.getMonth() + 1).padStart(2, '0');
  const day = String(et.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isTimeToShow(): boolean {
  const et = getCurrentEasternTime();
  const hour = et.getHours();
  if (hour < 14) return false;

  const dateKey = getEasternDateKey();
  const dismissed = localStorage.getItem(LOCALSTORAGE_KEY);
  return dismissed !== dateKey;
}

function dismissForToday(): void {
  const dateKey = getEasternDateKey();
  localStorage.setItem(LOCALSTORAGE_KEY, dateKey);
}

export const FridayCheckinModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [showHelpInput, setShowHelpInput] = useState(false);
  const [helpMessage, setHelpMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const user = useSelector(selectUser);

  const [submitCheckin, { isLoading }] = useSubmitFridayCheckinMutation();
  const { data: config } = useGetCheckinConfigQuery();

  useEffect(() => {
    if (config?.is_enabled && isTimeToShow()) {
      setOpen(true);
    }

    const interval = setInterval(() => {
      if (!open && config?.is_enabled && isTimeToShow()) {
        setOpen(true);
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [open, config?.is_enabled]);

  const handleDismiss = useCallback(() => {
    dismissForToday();
    setOpen(false);
    setShowHelpInput(false);
    setHelpMessage('');
    setSubmitted(false);
  }, []);

  const handleYes = useCallback(() => {
    handleDismiss();
  }, [handleDismiss]);

  const handleNo = useCallback(() => {
    setShowHelpInput(true);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  }, []);

  const handleSubmitHelp = useCallback(async () => {
    if (!helpMessage.trim() || !user) return;

    try {
      await submitCheckin({
        message: helpMessage.trim(),
        userName: `${user.first_name} ${user.last_name}`.trim() || user.email,
        userEmail: user.email,
      }).unwrap();
      setSubmitted(true);
      setTimeout(() => {
        handleDismiss();
      }, 1500);
    } catch {
      handleDismiss();
    }
  }, [helpMessage, user, submitCheckin, handleDismiss]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        handleDismiss();
      }
    },
    [handleDismiss],
  );

  if (!open) return null;

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
          <DialogTitle className="sr-only">Message Sent</DialogTitle>
          <DialogDescription className="sr-only">
            Your help request has been sent
          </DialogDescription>
          <div className="px-4 py-8 flex flex-col items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            </div>
            <p className="text-[13px] font-semibold text-zinc-900">
              Sent! Help is on the way.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (showHelpInput) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
          <div className="px-4 pt-4 pb-3 border-b border-zinc-100">
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-50">
                <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-[13px] font-semibold text-zinc-900 truncate">
                  Need Help?
                </DialogTitle>
                <DialogDescription className="text-[11px] text-zinc-500 mt-0.5">
                  Let us know what you need assistance with
                </DialogDescription>
              </div>
            </div>
          </div>

          <div className="px-4 pt-3 pb-1">
            <p className="text-[11px] font-medium text-zinc-500 mb-2">
              What do you need help with on your plate?
            </p>
            <textarea
              ref={textareaRef}
              value={helpMessage}
              onChange={(e) => setHelpMessage(e.target.value)}
              placeholder="e.g., I have too many record requests to process, need help with coverage reviews..."
              className="w-full h-24 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-md text-[12px] text-zinc-900 placeholder-zinc-400 resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  !e.shiftKey &&
                  helpMessage.trim()
                ) {
                  e.preventDefault();
                  handleSubmitHelp();
                }
              }}
            />
          </div>

          <div className="flex justify-end gap-2 px-4 pb-4 pt-2 border-t border-zinc-100 mt-2">
            <button
              type="button"
              onClick={() => {
                setShowHelpInput(false);
                setHelpMessage('');
              }}
              className="px-2.5 py-1 text-[10px] font-medium text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50 rounded transition-colors"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleSubmitHelp}
              disabled={!helpMessage.trim() || isLoading}
              className={cn(
                'flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold rounded transition-colors',
                helpMessage.trim() && !isLoading
                  ? 'bg-violet-600 text-white hover:bg-violet-700'
                  : 'bg-zinc-100 text-zinc-400 cursor-not-allowed',
              )}
            >
              <Send className="h-2.5 w-2.5" />
              {isLoading ? 'Sending...' : 'Submit'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
        <div className="px-4 pt-4 pb-3 border-b border-zinc-100">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-50">
              <ClipboardCheck className="h-3.5 w-3.5 text-violet-600" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-[13px] font-semibold text-zinc-900 truncate">
                Daily Check-In
              </DialogTitle>
              <DialogDescription className="text-[11px] text-zinc-500 mt-0.5">
                Task progress check
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="px-4 py-5">
          <p className="text-[13px] font-medium text-zinc-900 text-center">
            Are you on track to complete your tasks today?
          </p>
        </div>

        <div className="flex justify-end gap-2 px-4 pb-4 pt-3 border-t border-zinc-100">
          <button
            type="button"
            onClick={handleNo}
            className="px-2.5 py-1 text-[10px] font-medium text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50 rounded transition-colors"
          >
            No
          </button>
          <button
            type="button"
            onClick={handleYes}
            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded transition-colors"
          >
            <CheckCircle2 className="h-2.5 w-2.5" />
            Yes
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
